import React, { FC, useEffect, useState } from 'react';

import { Box, Combobox, Loader, useCombobox } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { personApi } from '@/shared/api/person.ts';
import { getFilteredOptions } from '@/shared/helpers/utils.ts';
import { LabelInput } from '@/shared/uikit/components/input';

import { IconX } from '@tabler/icons-react';

type Props = {
	initialValue?: number;
	label?: string;
	setPersonId(personId: number): void;
	onClear?(): void;
};

export const UserSearch: FC<Props> = ({
	initialValue,
	label = 'Сотрудник',
	setPersonId,
	onClear,
}) => {
	const combobox = useCombobox();
	const [value, setValue] = useState<string | undefined>(undefined);
	const [search, setSearch] = useState('');

	const {
		data: persons = [],
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ['persons'],
		queryFn: () => personApi.getPersons(),
	});
	persons?.map((elem) => {
		return { value: `${elem.id}`, label: elem.full_name };
	});

	const filteredOptions = getFilteredOptions(
		persons?.map((person) => person?.full_name),
		search,
		7,
	);

	const handleClearButtonClick = () => {
		onClear?.();
		setValue(undefined);
		setSearch('');

		combobox.closeDropdown();
	};

	const handleOptionSubmit = (optionValue: string) => {
		const person = persons?.find((person) => person?.full_name === optionValue);
		if (person) {
			setPersonId(person?.id);
			setValue(optionValue);
			setSearch(optionValue);
		}
		combobox.closeDropdown();
	};

	useEffect(() => {
		if (initialValue) {
			const person = persons?.find((person) => person.id === initialValue);

			if (person) {
				setValue(person.full_name);
				setSearch(person.full_name);
			}
		}
	}, [initialValue, persons]);

	const options = filteredOptions?.map((item) => {
		return (
			<Combobox.Option value={item} key={item} active={item === value}>
				{item}
			</Combobox.Option>
		);
	});

	return (
		<Combobox onOptionSubmit={handleOptionSubmit} store={combobox}>
			<Combobox.Target>
				<Box>
					<LabelInput
						label={label}
						placeholder="Иванов Иван Иванович"
						value={search}
						disabled={isLoading || isFetching}
						rightSection={
							isLoading || isFetching ? (
								<Loader size={'xs'} />
							) : value && onClear ? (
								<IconX onClick={handleClearButtonClick} cursor={'pointer'} />
							) : null
						}
						onClick={() => combobox.openDropdown()}
						onFocus={() => combobox.openDropdown()}
						onBlur={() => {
							combobox.closeDropdown();
							setSearch(value || '');
						}}
						onChange={(event) => {
							setSearch(event.currentTarget.value);
							combobox.openDropdown();
							combobox.updateSelectedOptionIndex();
						}}
					/>
				</Box>
			</Combobox.Target>

			<Combobox.Dropdown>
				<Combobox.Options>
					{options.length === 0 ? <Combobox.Empty>Nothing found</Combobox.Empty> : options}
				</Combobox.Options>
			</Combobox.Dropdown>
		</Combobox>
	);
};
